import { Task, TaskStatus } from '@lit/task';
import { LitElement, css, html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { api } from './api.js';

import { WireTypes } from '@flakiness/server/common/wireTypes.js';
import { ContextType } from '@lit/context';
import { linkStyles, pageStyles } from './components/cssstyles.js';
import { consume, contexts } from './contexts.js';
import { consumeOAuthError } from './oauthError.js';
import { PageNewOrganization } from './page-new-organization.js';
import { PageNewProject } from './page-new-project.js';
import { platformUrl } from './platformUrl.js';
import { RouteConfig } from './router.js';
import { tasks } from './tasks.js';

@customElement('page-user-projects')
export class PageUserProjects extends LitElement {
  static routes(): RouteConfig[] {
    return [{
      path: [ '/' ],
      render: (groups) => html`<page-user-projects></page-user-projects>`,
    }];
  }

  static url(): string {
    return platformUrl('/');
  }

  @consume({ context: contexts.user, subscribe: true })
  private _user?: ContextType<typeof contexts.user>;;

  @consume({ context: contexts.serverInfo, subscribe: true })
  private _serverInfo?: ContextType<typeof contexts.serverInfo>;

  @state() private _githubError?: string;

  override connectedCallback() {
    super.connectedCallback();
    this._githubError = consumeOAuthError();
  }

  private _dashboardTask = new Task(this, {
    args: () => [this._user],
    task: async ([user], { signal }) => {
      const result = await api.user.dashboard.GET(undefined, { signal });
      const orgs = new Map([...result.ownedOrgs, ...result.sharedOrgs].map(org => [org.orgSlug, org]));
      return {
        projects: result.projects,
        orgs: [...orgs.values()]
          .filter(org => org.access === 'owner' || org.access === 'admin')
          .sort((a, b) => a.orgSlug.localeCompare(b.orgSlug)),
      };
    }
  });

  private _discoverGithubProjectsTask = new Task(this, {
    // Superusers get a fresh serverInfo object every minute, so depend on the
    // availability flag only. Otherwise discovery would rerun every minute.
    args: () => [this._user, isGithubAvailable(this._serverInfo)] as const,
    task: async ([user, githubAvailable], { signal }) => {
      if (!githubAvailable || !user?.linkedProviders.includes('github'))
        return undefined;
      return await api.user.discoverGithubProjects.GET(undefined, { signal });
    }
  });

  private _pageMetadataTask = tasks.pageMetadata(this, {
    args: () => [],
    metadata: () => ({ title: 'Projects · Dashboard' }),
  });

  render() {
    return html`
      <user-header submenu=projects></user-header>
      <app-body medium>
        <h-box>
          <div class=title>Projects</div>
          ${this._isLoadingProjects() ? html`<sl-spinner></sl-spinner>` : nothing}
          <x-filler></x-filler>
          ${this._renderNewProject()}
        </h-box>
        ${this._renderGithubCallouts()}
        ${this._renderProjects()}
      </app-body>
      <app-footer></app-footer>
    `;
  }

  private _renderNewProject(size: 'small' | 'medium' = 'small') {
    return html`
      <sl-dropdown placement=bottom-end>
        <sl-button slot=trigger variant=success size=${size} caret ?disabled=${!this._user}>
          <sl-icon slot=prefix name=plus-circle></sl-icon>New project
        </sl-button>
        <nav class=new-project-menu aria-label="Create project">
          <sl-menu-label>Create in organization</sl-menu-label>
          ${this._dashboardTask.render({
            pending: () => html`<span class=menu-message>Loading organizations…</span>`,
            error: () => html`<span class=menu-message>Couldn't load organizations</span>`,
            complete: ({ orgs }) => orgs.length ? orgs.map(org => html`
              <a
                href=${org.restrictedProjectAccess ? nothing : PageNewProject.url({ orgSlug: org.orgSlug })}
                aria-disabled=${org.restrictedProjectAccess ? 'true' : nothing}
              ><sl-icon name=buildings></sl-icon>${org.orgSlug}</a>
            `) : html`<span class=menu-message>No organizations available</span>`,
          })}
          <sl-divider></sl-divider>
          <a href=${PageNewOrganization.url()}>
            <sl-icon name=folder-plus></sl-icon>New organization
          </a>
        </nav>
      </sl-dropdown>
    `;
  }

  private _renderGithubCallouts() {
    const needsConnect = this._user && isGithubAvailable(this._serverInfo) && !this._user.linkedProviders.includes('github');
    return html`
      ${this._githubError ? html`
        <fk-callout variant=danger>${this._githubError}</fk-callout>
      ` : nothing}
      ${needsConnect ? html`
        <fk-callout variant=tip>
          ${this._renderLinkGithub('Connect GitHub')}
        </fk-callout>
      ` : nothing}
      ${this._discoverGithubProjectsTask.render({
        complete: value => value?.needsReauth ? html`
          <fk-callout variant=warn>
            ${this._renderLinkGithub('Reconnect GitHub')}
          </fk-callout>
        ` : nothing,
        error: () => html`
          <fk-callout variant=danger>Couldn't load projects from GitHub. Please try again later.</fk-callout>
        `,
      })}
    `;
  }

  private _renderLinkGithub(label: string) {
    return html`
      <h-box>
        ${label} to see projects for your GitHub repositories.
        <x-filler></x-filler>
        <sl-button size=small href=${`/login/github/link?return_to=${encodeURIComponent(window.location.href)}`}>
          <sl-icon slot=prefix name=github></sl-icon>${label}
        </sl-button>
      </h-box>
    `;
  }

  private _renderProjects() {
    // GitHub projects exclude the ones the dashboard lists, so the lists don't overlap.
    const projects = [...this._dashboardTask.value?.projects ?? [], ...this._discoverGithubProjectsTask.value?.projects ?? []];
    if (projects.length)
      return html`<list-projects show-org .projects=${projects}></list-projects>`;
    if (this._isLoadingProjects())
      return nothing;
    const canCreateProject = this._dashboardTask.value?.orgs.some(org => !org.restrictedProjectAccess);
    return html`
      <section class=empty-state>
        <sl-icon class=empty-icon name=activity></sl-icon>
        <h2>Make every test run count</h2>
        <p>Catch regressions, uncover flaky tests, and watch your test suite improve. It starts with your first project.</p>
        ${canCreateProject ? this._renderNewProject('medium') : html`
          <sl-button href=${PageNewOrganization.url()} variant=success>
            <sl-icon slot=prefix name=folder-plus></sl-icon>Create organization
          </sl-button>
          <span class=empty-hint>First, create an organization for your projects.</span>
        `}
      </section>
    `;
  }

  private _isLoadingProjects() {
    return this._dashboardTask.status === TaskStatus.PENDING || this._discoverGithubProjectsTask.status === TaskStatus.PENDING;
  }

  static styles = [linkStyles, pageStyles, css`
    .new-project-menu {
      padding: var(--sl-spacing-x-small) 0;
      background: var(--sl-panel-background-color);
      border: var(--sl-panel-border-width) solid var(--sl-panel-border-color);
      border-radius: var(--sl-border-radius-medium);

      a, .menu-message {
        display: flex;
        align-items: center;
        gap: var(--sl-spacing-small);
        padding: var(--sl-spacing-x-small) var(--sl-spacing-medium);
      }

      a[href]:hover, a[href]:focus-visible {
        background: var(--sl-color-neutral-100);
        text-decoration: none;
      }

      a[aria-disabled], .menu-message {
        color: var(--sl-color-neutral-400);
      }

      sl-divider {
        --spacing: var(--sl-spacing-x-small);
      }
    }

    fk-contact-us {
      color: blue;
    }

    .title {
      font-size: var(--sl-font-size-2x-large);
    }

    .empty-state {
      display: flex;
      flex-direction: column;
      align-items: center;
      padding: var(--sl-spacing-3x-large) var(--sl-spacing-large);
      border: 1px solid var(--sl-panel-border-color);
      border-radius: var(--sl-border-radius-medium);
      text-align: center;

      .empty-icon {
        padding: var(--sl-spacing-large);
        border-radius: var(--sl-border-radius-circle);
        background: var(--sl-color-success-50);
        color: var(--sl-color-success-600);
        font-size: var(--sl-font-size-2x-large);
      }

      h2 {
        margin: var(--sl-spacing-large) 0 var(--sl-spacing-small);
        font-size: var(--sl-font-size-x-large);
        font-weight: var(--sl-font-weight-semibold);
      }

      p {
        max-width: 48ch;
        margin: 0 0 var(--sl-spacing-large);
        color: var(--sl-color-neutral-600);
        line-height: var(--sl-line-height-normal);
      }

      .empty-hint {
        margin-top: var(--sl-spacing-small);
        color: var(--sl-color-neutral-600);
        font-size: var(--sl-font-size-small);
      }
    }

    a {
      color: var(--sl-color-neutral-900);
      text-decoration: none;
    }

    demo-chart {
      height: 200px;
    }
  `];
}

// GitHub login and linking routes exist only when the server has a GitHub app,
// and single-user mode has no accounts to link.
function isGithubAvailable(serverInfo?: WireTypes.ServerInfo): boolean {
  return !!serverInfo?.enabledGithub && !serverInfo.isSingleUserMode;
}
